import { useState, useCallback, useRef } from 'react';
import { useUploadReportPhoto, useDetectFromPhoto, type PhotoMetadata, type DetectionResult } from '@cdf/api-client-react';
import { convertToJpeg } from '@/lib/image-convert';

export interface AttachedPhoto {
  id: string;
  blobKey: string | null;
  preview: string;
  metadata: PhotoMetadata | null;
  detection: DetectionResult | null;
  uploading: boolean;
  detecting: boolean;
  error: boolean;
}

export const MAX_PHOTOS = 5;

let photoIdCounter = 0;

export function useMultiPhoto() {
  const [photos, setPhotos] = useState<AttachedPhoto[]>([]);
  const [activePhotoId, setActivePhotoId] = useState<string | null>(null);
  const uploadMutation = useUploadReportPhoto();
  const detectMutation = useDetectFromPhoto();
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const galleryInputRef = useRef<HTMLInputElement>(null);
  const photosRef = useRef<AttachedPhoto[]>([]);
  photosRef.current = photos;

  const updatePhoto = useCallback((id: string, patch: Partial<AttachedPhoto>) => {
    setPhotos((prev) => prev.map((p) => (p.id === id ? { ...p, ...patch } : p)));
  }, []);

  const addPhoto = useCallback(
    async (file: File) => {
      const jpegFile = await convertToJpeg(file);
      const preview = URL.createObjectURL(jpegFile);
      const id = `photo-${++photoIdCounter}`;

      const newPhoto: AttachedPhoto = {
        id,
        blobKey: null,
        preview,
        metadata: null,
        detection: null,
        uploading: true,
        detecting: false,
        error: false,
      };

      setPhotos((prev) => [...prev, newPhoto]);
      setActivePhotoId(id);

      uploadMutation.mutate(
        { data: { photo: jpegFile } },
        {
          onSuccess: (res) => {
            updatePhoto(id, { blobKey: res.blobKey, metadata: res.metadata, uploading: false, detecting: true });
            detectMutation.mutate(
              { data: { blobKey: res.blobKey } },
              {
                onSuccess: (data) => updatePhoto(id, { detection: data.detection ?? null, detecting: false }),
                onError: () => updatePhoto(id, { detection: null, detecting: false }),
              }
            );
          },
          onError: () => updatePhoto(id, { uploading: false, error: true }),
        }
      );
    },
    [uploadMutation, detectMutation, updatePhoto]
  );

  const handlePhotoSelect = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = Array.from(e.target.files ?? []);
      if (files.length === 0) return;
      for (const file of files) {
        if (photosRef.current.length >= MAX_PHOTOS) break;
        await addPhoto(file);
      }
    },
    [addPhoto]
  );

  const removePhoto = useCallback(
    (id: string) => {
      const target = photosRef.current.find((p) => p.id === id);
      if (target) URL.revokeObjectURL(target.preview);
      setPhotos((prev) => prev.filter((p) => p.id !== id));
      setActivePhotoId((prev) => {
        if (prev !== id) return prev;
        const remaining = photosRef.current.filter((p) => p.id !== id);
        return remaining.length > 0 ? (remaining[0]?.id ?? null) : null;
      });
    },
    []
  );

  const clearPhotos = useCallback(() => {
    setPhotos((current) => {
      for (const photo of current) URL.revokeObjectURL(photo.preview);
      return [];
    });
    setActivePhotoId(null);
  }, []);

  const movePhoto = useCallback((id: string, direction: 'left' | 'right') => {
    setPhotos((prev) => {
      const idx = prev.findIndex((p) => p.id === id);
      if (idx === -1) return prev;
      const swapIdx = direction === 'left' ? idx - 1 : idx + 1;
      if (swapIdx < 0 || swapIdx >= prev.length) return prev;
      const next = [...prev];
      const tmp = next[idx]!;
      next[idx] = next[swapIdx]!;
      next[swapIdx] = tmp;
      return next;
    });
  }, []);

  const acceptPhotoLocation = useCallback(
    (id: string): { latitude: number; longitude: number } | null => {
      const photo = photosRef.current.find((p) => p.id === id);
      if (!photo?.metadata?.hasGps || photo.metadata.latitude == null || photo.metadata.longitude == null) {
        return null;
      }
      return { latitude: photo.metadata.latitude, longitude: photo.metadata.longitude };
    },
    []
  );

  const isUploading = photos.some((p) => p.uploading);
  const isDetecting = photos.some((p) => p.detecting);
  const hasUploadedPhoto = photos.some((p) => p.blobKey !== null);
  const blobKeys = photos.filter((p) => p.blobKey).map((p) => p.blobKey!) as string[];
  const canAddMore = photos.length < MAX_PHOTOS;

  return {
    photos,
    activePhotoId,
    setActivePhotoId,
    addPhoto,
    handlePhotoSelect,
    removePhoto,
    clearPhotos,
    movePhoto,
    acceptPhotoLocation,
    cameraInputRef,
    galleryInputRef,
    uploadMutation,
    detectMutation,
    isUploading,
    isDetecting,
    hasUploadedPhoto,
    blobKeys,
    canAddMore,
  };
}
